/**
 * 🔐 Auth Guard for Coach Cedar
 * Protects routes using Auth0 and Coach Cedar authentication
 * Cross-platform support for web and native
 */

import React from 'react';
import { useAuth0 } from '@auth0/auth0-react'; // Web-only import (App.tsx uses Auth0Provider)
import { useAuth } from '../contexts/AuthContext';
import { Loader } from 'lucide-react';
import { Auth0LoginScreen } from '../pages/Auth0LoginScreen';
import { LoginScreenWeb } from '../screens/LoginScreenWeb';

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { isLoading: auth0Loading, isAuthenticated: auth0Authenticated, error: auth0Error } = useAuth0();
  const { user: coachCedarUser, isLoading: coachCedarLoading } = useAuth();

  // 🔧 DEV MODE: Bypass authentication for localhost (web only)
  if (typeof window !== 'undefined') {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) {
      console.log('🔧 DEV MODE: Bypassing AuthGuard for localhost development');
      return <>{children}</>;
    }
  }

  // Show loading spinner while Auth0 is initializing
  if (auth0Loading || coachCedarLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  // Show error if Auth0 failed to initialize
  if (auth0Error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Authentication Error</h2>
            <p className="text-red-700 text-sm mb-4">{auth0Error.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check for token-based authentication (bypasses Auth0) - web only
  let hasInviteToken = false;
  let isOnCallbackRoute = false;
  
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    hasInviteToken = urlParams.get('token')?.startsWith('invite_') || false;
    isOnCallbackRoute = window.location.pathname.includes('/auth/callback');
  }
  
  // Check authentication status
  // User must be authenticated with Auth0 AND have a Coach Cedar user account
  // OR have a valid invite token and Coach Cedar user account
  const isFullyAuthenticated = (auth0Authenticated && coachCedarUser) || (hasInviteToken && coachCedarUser);

  // Show login screen if not authenticated with Auth0 AND no invite token
  if (!auth0Authenticated && !hasInviteToken) {
    return typeof window !== 'undefined' ? <LoginScreenWeb /> : <Auth0LoginScreen />;
  }

  // If Auth0 authenticated but no Coach Cedar user, they're likely in the callback process
  // Don't show loading screen if we're on the callback route (let Auth0Callback handle it)
  // Also don't show loading for invite token users (let Splash handle token authentication)
  if (auth0Authenticated && !coachCedarUser && !isOnCallbackRoute && !hasInviteToken) {
    // IMPORTANT: This state can happen after a user logs out and we clear our app session,
    // but Auth0 still considers the user authenticated. Showing an infinite spinner here
    // traps the user. Instead, send them back through the standard login UX.
    return typeof window !== 'undefined' ? <LoginScreenWeb /> : <Auth0LoginScreen />;
  }

  // User is fully authenticated, show the protected content
  return <>{children}</>;
};